"use client"

import { useCallback, useContext, useEffect, useState } from "react"
import { getCollectionDefaultSettings } from "./CollectionConfig"
import { TypesenseContext } from "./TypesenseProvider"
import { getTypesenseKey, UserContext } from "./getTypesenseKey"

export interface Document {
  id: string
  label: string
}

export type TypesenseSearchProps = {
  collectionName: string
  valueAttribute?: string
  displayAttr?: string
  value: string
  customOption?: (item: object) => string
  useDefaultCollectionAttributes?: boolean
  context?: UserContext
}

export const useTypesenseSearch = (props: TypesenseSearchProps) => {
  let searchProps = { ...props }
  if (props.useDefaultCollectionAttributes) {
    searchProps = {
      ...getCollectionDefaultSettings(props.collectionName),
      ...searchProps,
    }
  }
  const { apiKey, multiCollectionKeysV2, typesenseHost } =
    useContext(TypesenseContext)

  // For useTypesenseSearch, we work with collection names directly
  const getCollectionApiKey = (collectionName: string) => {
    const isPrivateCollection = collectionName?.startsWith("private:")

    if (!isPrivateCollection) {
      return apiKey
    }

    if (!multiCollectionKeysV2) {
      return undefined
    }

    return getTypesenseKey({
      multiCollectionKeysV2: multiCollectionKeysV2,
      searchApiKey: apiKey,
      collectionName: collectionName,
      context: searchProps.context || "generic",
      usePermissions: undefined,
    })
  }

  const collectionApiKey = getCollectionApiKey(searchProps.collectionName)
  const [retry, setRetry] = useState(5)
  const [displayValue, setDisplayValue] = useState("")
  const [document, setDocument] = useState<object>()

  const searchParams = {
    query_by: searchProps.valueAttribute,
    query_by_weights: "1",
    typoTokensThreshold: 0,
    page: 50,
  }

  const createQueryString = useCallback(
    (name: string, value: string) => {
      const params = new URLSearchParams(searchParams.toString())
      params.set(name, value)

      return params.toString()
    },
    [searchParams],
  )

  const displayValueSelector = useCallback(
    (document?: object) => {
      if (!document) {
        return ""
      }
      if (searchProps.customOption) {
        return searchProps.customOption(document)
      }
      return document[(searchProps.displayAttr || "") as keyof object]
    },
    [searchProps.customOption, searchProps.displayAttr],
  )

  const searchData = useCallback(
    async (collectionName: string) => {
      if (!collectionApiKey || retry <= 0 || !searchProps.value) return []
      try {
        setRetry((current) => current - 1)
        // TODO interim code, use typesense package to call Typesense API
        const response = await fetch(
          `${typesenseHost}/search/collections/${collectionName}/documents/search?${createQueryString("q", (searchProps.value || "").toString())}&${createQueryString("query_by", searchParams.query_by || "*")}&${createQueryString("per_page", searchParams.page.toString())}`,
          {
            headers: {
              "X-Typesense-Api-Key": collectionApiKey,
            },
          },
        )
        const data = await response.json()
        let selectDoc: object | undefined = undefined
        if (data?.hits && data.hits.length > 0) {
          data.hits.map((hit: { document: object }) => {
            const _document: object = hit.document as object
            const docValue =
              _document[searchProps.valueAttribute as keyof object]
            if (searchProps.value === docValue) {
              selectDoc = _document
            }
          })
          const labels = displayValueSelector(selectDoc)
          setDisplayValue(labels)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          setDocument(selectDoc)
          return selectDoc
        }
      } catch (error) {
        if (error instanceof Error) {
          console.error(error.message)
        }
        console.error("There is an error when retrieving data")
      }

      // retry 5 times when no data or error happens
      window.setTimeout(
        () => searchData(searchProps.collectionName),
        100 * (6 - retry),
      )
      return []
    },
    [collectionApiKey, searchProps.value],
  )

  useEffect(() => {
    searchData(searchProps.collectionName)
  }, [searchProps.collectionName, searchData])

  return { displayValue, document }
}
